/**
 * Reclaim shared catalog: duplicate the quotation request for the current lead.
 * Route: /store/catalog/:token/reclaim
 *
 * Flujo:
 * - Con sesión: auto-reclaim → navega a /store/quotations/:id.
 * - Sin sesión: muestra "Iniciar sesión" + formulario de registro inline.
 *   El registro envía código de verificación al email. Tras verificar y loguearse,
 *   el token pendiente en localStorage dispara el reclaim automático.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import { getWorkerApiBase, postWorkerApi } from '@/lib/worker-api';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, LogIn, UserPlus, Mail } from 'lucide-react';

export default function StoreCatalogReclaim() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-session'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Formulario inline de registro (sin sesión)
  const [showRegister, setShowRegister] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regSent, setRegSent] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Falta el token del enlace.');
      return;
    }
    // Persistir token para recuperarlo tras login/verificación.
    localStorage.setItem('pendingReclaimToken', token);

    let cancelled = false;
    (async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        if (!cancelled) setStatus('no-session');
        return;
      }
      const base = getWorkerApiBase();
      const res = await fetch(`${base}/api/quotation-request/reclaim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.session.access_token}`,
        },
        body: JSON.stringify({ token }),
      });
      if (cancelled) return;
      const data = (await res.json().catch(() => ({}))) as { request_id?: string; error?: string };
      if (res.ok && data.request_id) {
        localStorage.removeItem('pendingReclaimToken');
        queryClient.invalidateQueries({ queryKey: ['quotation-request-cart'] });
        navigate(`/store/quotations/${data.request_id}`, { replace: true });
        return;
      }
      setStatus('error');
      setErrorMessage((data?.error as string) || 'No se pudo reclamar la cotización.');
    })();
    return () => { cancelled = true; };
  }, [token, navigate, queryClient]);

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = regEmail.trim().toLowerCase();
    const name = regName.trim();
    if (!email || !name) return;
    setRegLoading(true);
    const res = await postWorkerApi<{ success?: boolean }>('/api/send-verification-code', { email, name });
    setRegLoading(false);
    if (res.ok) {
      setRegSent(true);
    } else {
      setErrorMessage(res.error ?? 'Error al enviar el código de verificación.');
      setStatus('error');
    }
  };

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: 'Reclamar cotización' },
  ];

  const returnTo = `/store/catalog/${token}/reclaim`;

  // Código de verificación enviado → pantalla de espera
  if (regSent) {
    return (
      <div className="space-y-6">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center max-w-md mx-auto space-y-4">
          <Mail className="h-10 w-10 text-green-600 mx-auto" />
          <h2 className="text-lg font-bold text-green-900">Revisa tu email</h2>
          <p className="text-sm text-green-800">
            Hemos enviado un código de verificación a <strong>{regEmail}</strong>.
            Abre el enlace del correo para crear tu contraseña y activar tu cuenta.
          </p>
          <p className="text-xs text-green-700">
            Una vez verificada tu cuenta e iniciada sesión, la cotización se reclamará automáticamente.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'no-session') {
    return (
      <div className="space-y-6">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 max-w-md mx-auto space-y-5">
          <p className="text-amber-900 text-center font-medium">Para reclamar esta cotización necesitas una cuenta.</p>

          {/* Opción 1: Login */}
          <div className="text-center">
            <Button asChild className="gap-2">
              <Link to={`/store/login?redirect=${encodeURIComponent(returnTo)}`} state={{ from: returnTo }}>
                <LogIn className="h-4 w-4" />
                Ya tengo cuenta
              </Link>
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-amber-300" />
            <span className="text-xs text-amber-600 font-medium">o</span>
            <div className="h-px flex-1 bg-amber-300" />
          </div>

          {/* Opción 2: Registro inline */}
          {!showRegister ? (
            <div className="text-center">
              <Button variant="outline" className="gap-2" onClick={() => setShowRegister(true)}>
                <UserPlus className="h-4 w-4" />
                No tengo cuenta — crear una
              </Button>
            </div>
          ) : (
            <form onSubmit={handleRegisterSubmit} className="space-y-3">
              <div>
                <Label htmlFor="reg-name">Nombre</Label>
                <Input
                  id="reg-name"
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  placeholder="Tu nombre"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="reg-email">Email</Label>
                <Input
                  id="reg-email"
                  type="email"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="mt-1"
                  required
                />
              </div>
              <Button type="submit" className="w-full gap-2" disabled={regLoading}>
                {regLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {regLoading ? 'Enviando...' : 'Enviar código de verificación'}
              </Button>
              <p className="text-xs text-amber-700 text-center">
                Recibirás un email con un enlace para crear tu contraseña.
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="space-y-6">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-8 text-center max-w-md mx-auto">
          <p className="text-zinc-700">{errorMessage}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            <Button asChild variant="outline">
              <Link to={`/store/catalog/${token}`}>Volver al catálogo</Link>
            </Button>
            <Button asChild>
              <Link to="/store">Ir al store</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StoreBreadcrumbs segments={breadcrumbs} />
      <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500 mx-auto mb-3" />
        <p className="text-zinc-600">Reclamando cotización...</p>
      </div>
    </div>
  );
}
